/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { AvailabilitySets } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { ComputeManagementClient } from "../computeManagementClient.js";
import {
  SimplePollerLike,
  OperationState,
  createHttpPoller,
} from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import {
  AvailabilitySet,
  AvailabilitySetsListBySubscriptionNextOptionalParams,
  AvailabilitySetsListBySubscriptionOptionalParams,
  AvailabilitySetsListBySubscriptionResponse,
  AvailabilitySetsListNextOptionalParams,
  AvailabilitySetsListOptionalParams,
  AvailabilitySetsListResponse,
  VirtualMachineSize,
  AvailabilitySetsListAvailableSizesNextOptionalParams,
  AvailabilitySetsListAvailableSizesOptionalParams,
  AvailabilitySetsListAvailableSizesResponse,
  AvailabilitySetsGetOptionalParams,
  AvailabilitySetsGetResponse,
  AvailabilitySetsCreateOrUpdateOptionalParams,
  AvailabilitySetsCreateOrUpdateResponse,
  AvailabilitySetUpdate,
  AvailabilitySetsUpdateOptionalParams,
  AvailabilitySetsUpdateResponse,
  AvailabilitySetsDeleteOptionalParams,
  AvailabilitySetsCancelMigrationToVirtualMachineScaleSetOptionalParams,
  AvailabilitySetsConvertToVirtualMachineScaleSetOptionalParams,
  AvailabilitySetsConvertToVirtualMachineScaleSetResponse,
  MigrateToVirtualMachineScaleSetInput,
  AvailabilitySetsStartMigrationToVirtualMachineScaleSetOptionalParams,
  AvailabilitySetsValidateMigrationToVirtualMachineScaleSetOptionalParams,
  AvailabilitySetsListBySubscriptionNextResponse,
  AvailabilitySetsListNextResponse,
  AvailabilitySetsListAvailableSizesNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing AvailabilitySets operations. */
export class AvailabilitySetsImpl implements AvailabilitySets {
  private readonly client: ComputeManagementClient;

  /**
   * Initialize a new instance of the class AvailabilitySets class.
   * @param client Reference to the service client
   */
  constructor(client: ComputeManagementClient) {
    this.client = client;
  }

  /**
   * Lists all availability sets in a subscription.
   * @param options The options parameters.
   */
  public listBySubscription(
    options?: AvailabilitySetsListBySubscriptionOptionalParams,
  ): PagedAsyncIterableIterator<AvailabilitySet> {
    const iter = this.listBySubscriptionPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySubscriptionPagingPage(options, settings);
      },
    };
  }

  private async *listBySubscriptionPagingPage(
    options?: AvailabilitySetsListBySubscriptionOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<AvailabilitySet[]> {
    let result: AvailabilitySetsListBySubscriptionResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySubscription(options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySubscriptionNext(continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySubscriptionPagingAll(
    options?: AvailabilitySetsListBySubscriptionOptionalParams,
  ): AsyncIterableIterator<AvailabilitySet> {
    for await (const page of this.listBySubscriptionPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all availability sets in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    options?: AvailabilitySetsListOptionalParams,
  ): PagedAsyncIterableIterator<AvailabilitySet> {
    const iter = this.listPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    options?: AvailabilitySetsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<AvailabilitySet[]> {
    let result: AvailabilitySetsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    options?: AvailabilitySetsListOptionalParams,
  ): AsyncIterableIterator<AvailabilitySet> {
    for await (const page of this.listPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Lists all available virtual machine sizes that can be used to create a new virtual machine in an
   * existing availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  public listAvailableSizes(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsListAvailableSizesOptionalParams,
  ): PagedAsyncIterableIterator<VirtualMachineSize> {
    const iter = this.listAvailableSizesPagingAll(
      resourceGroupName,
      availabilitySetName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAvailableSizesPagingPage(
          resourceGroupName,
          availabilitySetName,
          options,
          settings,
        );
      },
    };
  }

  private async *listAvailableSizesPagingPage(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsListAvailableSizesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<VirtualMachineSize[]> {
    let result: AvailabilitySetsListAvailableSizesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAvailableSizes(
        resourceGroupName,
        availabilitySetName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAvailableSizesNext(
        resourceGroupName,
        availabilitySetName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAvailableSizesPagingAll(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsListAvailableSizesOptionalParams,
  ): AsyncIterableIterator<VirtualMachineSize> {
    for await (const page of this.listAvailableSizesPagingPage(
      resourceGroupName,
      availabilitySetName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all availability sets in a subscription.
   * @param options The options parameters.
   */
  private _listBySubscription(
    options?: AvailabilitySetsListBySubscriptionOptionalParams,
  ): Promise<AvailabilitySetsListBySubscriptionResponse> {
    return this.client.sendOperationRequest(
      { options },
      listBySubscriptionOperationSpec,
    );
  }

  /**
   * Lists all availability sets in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    options?: AvailabilitySetsListOptionalParams,
  ): Promise<AvailabilitySetsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, options },
      listOperationSpec,
    );
  }

  /**
   * Retrieves information about an availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsGetOptionalParams,
  ): Promise<AvailabilitySetsGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, options },
      getOperationSpec,
    );
  }

  /**
   * Create or update an availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param parameters Parameters supplied to the Create Availability Set operation.
   * @param options The options parameters.
   */
  createOrUpdate(
    resourceGroupName: string,
    availabilitySetName: string,
    parameters: AvailabilitySet,
    options?: AvailabilitySetsCreateOrUpdateOptionalParams,
  ): Promise<AvailabilitySetsCreateOrUpdateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, parameters, options },
      createOrUpdateOperationSpec,
    );
  }

  /**
   * Update an availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param parameters Parameters supplied to the Update Availability Set operation.
   * @param options The options parameters.
   */
  update(
    resourceGroupName: string,
    availabilitySetName: string,
    parameters: AvailabilitySetUpdate,
    options?: AvailabilitySetsUpdateOptionalParams,
  ): Promise<AvailabilitySetsUpdateResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, parameters, options },
      updateOperationSpec,
    );
  }

  /**
   * Delete an availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  delete(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsDeleteOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, options },
      deleteOperationSpec,
    );
  }

  /**
   * Cancel the migration operation on an Availability Set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  cancelMigrationToVirtualMachineScaleSet(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsCancelMigrationToVirtualMachineScaleSetOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, options },
      cancelMigrationToVirtualMachineScaleSetOperationSpec,
    );
  }

  /**
   * Create a new Flexible Virtual Machine Scale Set and migrate all the Virtual Machines in the
   * Availability Set. This does not trigger a downtime on the Virtual Machines.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  async beginConvertToVirtualMachineScaleSet(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsConvertToVirtualMachineScaleSetOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<AvailabilitySetsConvertToVirtualMachineScaleSetResponse>,
      AvailabilitySetsConvertToVirtualMachineScaleSetResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<AvailabilitySetsConvertToVirtualMachineScaleSetResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined =
        undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, availabilitySetName, options },
      spec: convertToVirtualMachineScaleSetOperationSpec,
    });
    const poller = await createHttpPoller<
      AvailabilitySetsConvertToVirtualMachineScaleSetResponse,
      OperationState<AvailabilitySetsConvertToVirtualMachineScaleSetResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Create a new Flexible Virtual Machine Scale Set and migrate all the Virtual Machines in the
   * Availability Set. This does not trigger a downtime on the Virtual Machines.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  async beginConvertToVirtualMachineScaleSetAndWait(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsConvertToVirtualMachineScaleSetOptionalParams,
  ): Promise<AvailabilitySetsConvertToVirtualMachineScaleSetResponse> {
    const poller = await this.beginConvertToVirtualMachineScaleSet(
      resourceGroupName,
      availabilitySetName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Start migration operation on an Availability Set to move its Virtual Machines to a Virtual Machine
   * Scale Set. This should be followed by a migrate operation on each Virtual Machine that triggers a
   * downtime on the Virtual Machine.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param parameters Parameters supplied to the migrate operation on the availability set.
   * @param options The options parameters.
   */
  startMigrationToVirtualMachineScaleSet(
    resourceGroupName: string,
    availabilitySetName: string,
    parameters: MigrateToVirtualMachineScaleSetInput,
    options?: AvailabilitySetsStartMigrationToVirtualMachineScaleSetOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, parameters, options },
      startMigrationToVirtualMachineScaleSetOperationSpec,
    );
  }

  /**
   * Validates that the Virtual Machines in the Availability Set can be migrated to the provided Virtual
   * Machine Scale Set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param parameters Parameters supplied to the migrate operation on the availability set.
   * @param options The options parameters.
   */
  validateMigrationToVirtualMachineScaleSet(
    resourceGroupName: string,
    availabilitySetName: string,
    parameters: MigrateToVirtualMachineScaleSetInput,
    options?: AvailabilitySetsValidateMigrationToVirtualMachineScaleSetOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, parameters, options },
      validateMigrationToVirtualMachineScaleSetOperationSpec,
    );
  }

  /**
   * Lists all available virtual machine sizes that can be used to create a new virtual machine in an
   * existing availability set.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param options The options parameters.
   */
  private _listAvailableSizes(
    resourceGroupName: string,
    availabilitySetName: string,
    options?: AvailabilitySetsListAvailableSizesOptionalParams,
  ): Promise<AvailabilitySetsListAvailableSizesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, options },
      listAvailableSizesOperationSpec,
    );
  }

  /**
   * ListBySubscriptionNext
   * @param nextLink The nextLink from the previous successful call to the ListBySubscription method.
   * @param options The options parameters.
   */
  private _listBySubscriptionNext(
    nextLink: string,
    options?: AvailabilitySetsListBySubscriptionNextOptionalParams,
  ): Promise<AvailabilitySetsListBySubscriptionNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listBySubscriptionNextOperationSpec,
    );
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    nextLink: string,
    options?: AvailabilitySetsListNextOptionalParams,
  ): Promise<AvailabilitySetsListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listNextOperationSpec,
    );
  }

  /**
   * ListAvailableSizesNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param availabilitySetName The name of the availability set.
   * @param nextLink The nextLink from the previous successful call to the ListAvailableSizes method.
   * @param options The options parameters.
   */
  private _listAvailableSizesNext(
    resourceGroupName: string,
    availabilitySetName: string,
    nextLink: string,
    options?: AvailabilitySetsListAvailableSizesNextOptionalParams,
  ): Promise<AvailabilitySetsListAvailableSizesNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, availabilitySetName, nextLink, options },
      listAvailableSizesNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listBySubscriptionOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Compute/availabilitySets",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySetListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.expand],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySetListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySet,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySet,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySet,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}",
  httpMethod: "DELETE",
  responses: {
    200: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const cancelMigrationToVirtualMachineScaleSetOperationSpec: coreClient.OperationSpec =
  {
    path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}/cancelMigrationToVirtualMachineScaleSet",
    httpMethod: "POST",
    responses: {
      204: {},
      default: {
        bodyMapper: Mappers.CloudError,
      },
    },
    queryParameters: [Parameters.apiVersion],
    urlParameters: [
      Parameters.$host,
      Parameters.subscriptionId,
      Parameters.resourceGroupName,
      Parameters.availabilitySetName,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
const convertToVirtualMachineScaleSetOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}/convertToVirtualMachineScaleSet",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper:
        Mappers.AvailabilitySetsConvertToVirtualMachineScaleSetHeaders,
    },
    201: {
      headersMapper:
        Mappers.AvailabilitySetsConvertToVirtualMachineScaleSetHeaders,
    },
    202: {
      headersMapper:
        Mappers.AvailabilitySetsConvertToVirtualMachineScaleSetHeaders,
    },
    204: {
      headersMapper:
        Mappers.AvailabilitySetsConvertToVirtualMachineScaleSetHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters2,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const startMigrationToVirtualMachineScaleSetOperationSpec: coreClient.OperationSpec =
  {
    path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}/startMigrationToVirtualMachineScaleSet",
    httpMethod: "POST",
    responses: {
      204: {},
      default: {
        bodyMapper: Mappers.CloudError,
      },
    },
    requestBody: Parameters.parameters3,
    queryParameters: [Parameters.apiVersion],
    urlParameters: [
      Parameters.$host,
      Parameters.subscriptionId,
      Parameters.resourceGroupName,
      Parameters.availabilitySetName,
    ],
    headerParameters: [Parameters.accept, Parameters.contentType],
    mediaType: "json",
    serializer,
  };
const validateMigrationToVirtualMachineScaleSetOperationSpec: coreClient.OperationSpec =
  {
    path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}/validateMigrationToVirtualMachineScaleSet",
    httpMethod: "POST",
    responses: {
      204: {},
      default: {
        bodyMapper: Mappers.CloudError,
      },
    },
    requestBody: Parameters.parameters3,
    queryParameters: [Parameters.apiVersion],
    urlParameters: [
      Parameters.$host,
      Parameters.subscriptionId,
      Parameters.resourceGroupName,
      Parameters.availabilitySetName,
    ],
    headerParameters: [Parameters.accept, Parameters.contentType],
    mediaType: "json",
    serializer,
  };
const listAvailableSizesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Compute/availabilitySets/{availabilitySetName}/vmSizes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualMachineSizeListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listBySubscriptionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySetListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.AvailabilitySetListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAvailableSizesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.VirtualMachineSizeListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.resourceGroupName,
    Parameters.availabilitySetName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
